import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Receipt,
  ShoppingCart,
  ArrowLeftRight,
  ClipboardList,
  BarChart3,
  Package,
  FileText,
  ArrowUpRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface QuickAction {
  id: string;
  title: string;
  description?: string;
  icon: typeof Receipt;
  href: string;
  color: string;
  bgColor: string;
  permission?: string;
}

interface QuickActionsProps {
  className?: string;
}

function QuickActionsComponent({ className }: QuickActionsProps) {
  const { t } = useTranslation(['dashboard', 'common']);

  const actions: QuickAction[] = [
    {
      id: 'new-sale',
      title: t('quickLinks.newSale'),
      description: t('quickLinks.newSaleDesc'),
      icon: Receipt,
      href: '/invoices/sales/new',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-500',
      permission: 'invoices:create',
    },
    {
      id: 'new-purchase',
      title: t('quickLinks.newPurchase'),
      description: t('quickLinks.newPurchaseDesc'),
      icon: ShoppingCart,
      href: '/invoices/purchases/new',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-500',
      permission: 'invoices:create',
    },
    {
      id: 'new-transfer',
      title: t('quickLinks.newTransfer'),
      description: t('quickLinks.newTransferDesc'),
      icon: ArrowLeftRight,
      href: '/invoices/transfers/new',
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-500',
      permission: 'invoices:create',
    },
    {
      id: 'new-issue',
      title: t('quickLinks.newIssue'),
      description: t('quickLinks.newIssueDesc'),
      icon: ArrowUpRight,
      href: '/invoices/issues/new',
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-500',
      permission: 'invoices:create',
    },
    {
      id: 'stock-take',
      title: t('quickLinks.stockTake'),
      description: t('quickLinks.stockTakeDesc'),
      icon: ClipboardList,
      href: '/stock-take',
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-500',
      permission: 'inventory:stocktake',
    },
    {
      id: 'reports',
      title: t('quickLinks.reports'),
      description: t('quickLinks.reportsDesc'),
      icon: BarChart3,
      href: '/reports',
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-500',
      permission: 'reports:view',
    },
    {
      id: 'inventory',
      title: t('quickLinks.inventory'),
      description: t('quickLinks.inventoryDesc'),
      icon: Package,
      href: '/inventory',
      color: 'text-teal-600 dark:text-teal-400',
      bgColor: 'bg-teal-500',
      permission: 'inventory:view',
    },
    {
      id: 'invoices',
      title: t('quickLinks.invoices'),
      description: t('quickLinks.invoicesDesc'),
      icon: FileText,
      href: '/invoices',
      color: 'text-indigo-600 dark:text-indigo-400',
      bgColor: 'bg-indigo-500',
      permission: 'invoices:view',
    },
  ];

  return (
    <div
      className={cn(
        'bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6 shadow-sm',
        className
      )}
    >
      <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">
        {t('quickActions')}
      </h2>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {actions.map((action) => (
          <QuickActionButton key={action.id} action={action} />
        ))}
      </div>
    </div>
  );
}

interface QuickActionButtonProps {
  action: QuickAction;
}

function QuickActionButton({ action }: QuickActionButtonProps) {
  const Icon = action.icon;

  return (
    <Link
      to={action.href}
      className={cn(
        'flex flex-col items-center justify-center p-4 rounded-lg',
        'border border-secondary-200 dark:border-secondary-700',
        'bg-white dark:bg-secondary-800',
        'hover:bg-secondary-50 dark:hover:bg-secondary-700',
        'hover:border-secondary-300 dark:hover:border-secondary-600',
        'transition-all duration-200 group',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-800'
      )}
    >
      <div
        className={cn(
          'rounded-full p-3 mb-2 transition-transform group-hover:scale-110',
          action.bgColor
        )}
      >
        <Icon className="h-5 w-5 text-white" aria-hidden="true" />
      </div>
      <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300 text-center">
        {action.title}
      </span>
      {action.description && (
        <span className="text-xs text-secondary-500 dark:text-secondary-400 text-center mt-0.5 hidden sm:block">
          {action.description}
        </span>
      )}
    </Link>
  );
}

export const QuickActions = memo(QuickActionsComponent);
export default QuickActions;
